/** Accept national US numbers or a single leading country code; never truncate. */
export function normalizeHostPhone(raw: string): string | null {
  if (!/^\+?[\d\s().-]*$/.test(raw.trim())) return null;
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10 && !raw.trim().startsWith("+")) return digits;
  if (digits.length === 11 && digits.startsWith("1")) return digits.slice(1);
  return null;
}

export function formatHostPhone(raw: string): string {
  const national = normalizeHostPhone(raw);
  // Preserve unsupported input so staff can correct it instead of changing identity.
  if (!national) return raw;
  return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
}

export type HostLookupQuery =
  | { kind: "phone"; value: string }
  | { kind: "name"; value: string }
  | { kind: "email"; value: string };

/** Validate the deliberately narrow lookup contract before any request leaves the tablet. */
export function parseHostLookupQuery(raw: string): HostLookupQuery | null {
  const value = raw.trim();
  if (!value || value.length > 254 || /[\u0000-\u001F\u007F]/.test(value)) return null;
  const phone = normalizeHostPhone(value);
  if (phone) return { kind: "phone", value: phone };
  // This is the same phone-like classification used by the lookup boundary:
  // a partial number must not quietly become a name search.
  if (/^\+?[\d\s().-]+$/.test(value) || value.includes("*")) return null;
  if (value.includes("@")) {
    return /^[^\s@]+@[^\s@]+$/i.test(value)
      ? { kind: "email", value }
      : null;
  }
  if (value.length > 120) return null;
  const words = value.split(/\s+/);
  if (words.length > 6 || value.replace(/[^\p{L}\p{N}]/gu, "").length < 2) return null;
  return { kind: "name", value: words.join(" ") };
}

export function hostLookupError(raw: string): string {
  const value = raw.trim();
  if (!value) return "Enter a full phone number, name, or email address.";
  if (value.includes("@")) return "Enter a complete email address; partial email searches are not supported.";
  if (value.includes("*")) return "Wildcard searches are not supported.";
  if (value.length > 254) return "Email searches must be at most 254 characters.";
  if (value.length > 120 || value.split(/\s+/).length > 6) return "Name searches must be at most 120 characters and six words.";
  return "Enter a full 10-digit US phone number, a name with at least two letters or numbers, or a complete email address.";
}

export interface HostIntakeDraft {
  phone: string;
  firstName: string;
  lastName: string;
  partySize: number;
  guestsPresent?: number;
  roomPref: "any" | "lounge" | "library" | "parlor";
  parlorOk: boolean;
  c33Priority: boolean;
  celebNotes: string;
  hostNotes: string;
  menuIntent: "neat" | "cocktails" | "food" | null;
  customerId: string | null;
  newGuest?: boolean;
  intakeMode?: "seat_now" | "waitlist";
  intakeRequestId?: string;
}

/** Both queue intake and immediate seating create the same guest/preferences. */
export function buildHostIntakePayload(draft: HostIntakeDraft) {
  const phone = normalizeHostPhone(draft.phone);
  const isNewGuest = draft.newGuest === true;
  if (draft.phone.trim() && !phone) {
    throw new Error("Enter a 10-digit US phone number, optionally starting with +1.");
  }
  if (!phone && (!isNewGuest || draft.intakeMode !== "seat_now")) {
    throw new Error("A phone number is required to add a party to the waitlist. Enter a 10-digit US phone number, optionally starting with +1.");
  }
  if (isNewGuest && !draft.firstName.trim()) throw new Error("Enter the guest's first name.");
  if (isNewGuest && (!draft.intakeMode || !draft.intakeRequestId)) throw new Error("Could not start this new guest intake. Start another party and try again.");
  const present = draft.guestsPresent ?? 1;
  if (!Number.isInteger(present) || present < 1 || present > draft.partySize) throw new Error("Guests present must be between 1 and the party size.");
  const labels = { neat: "🥃 Neat Pours Only", cocktails: "🍸 Cocktails & Neat Pours", food: "🍽️ Cocktails & Food" };
  let notes = draft.hostNotes.trim();
  if (draft.menuIntent) notes = `[Intent: ${labels[draft.menuIntent]}]${notes ? " — " + notes : ""}`;
  return {
    action: "add_entry",
    phone: phone ?? null,
    first_name_at_intake: draft.firstName.trim() || null,
    party_size: draft.partySize,
    arrived_count: present,
    preferred_room: draft.roomPref,
    parlor_holdover_ok: draft.parlorOk,
    c33_priority: draft.c33Priority,
    celebration_notes: draft.celebNotes.trim() || null,
    host_notes: notes || null,
    customer_id: draft.customerId,
    ...(draft.customerId === null && {
      new_guest_first_name: draft.firstName.trim() || null,
      new_guest_last_name: draft.lastName.trim() || null,
    }),
    ...(isNewGuest && {
      new_guest: true,
      intake_mode: draft.intakeMode,
      intake_request_id: draft.intakeRequestId,
    }),
  };
}

export function hostIntakeError(error: unknown): string {
  const message = error instanceof Error ? error.message : "Failed to add party.";
  if (message.includes("active_entry_exists")) return "This number is already on the waitlist. Resolve the existing entry first.";
  if (message.includes("intake_request_conflict")) return "This saved attempt no longer matches the party details. Check the dashboard before starting another party.";
  if (message.includes("customer_phone_mismatch") || message.includes("invalid_customer")) return "The selected guest no longer matches this phone number. Look up the number again and confirm the guest.";
  if (message.includes("Internal error")) return "Could not save this party. Check the dashboard before retrying.";
  return message;
}
